/**
 * Main SolarPredict Application
 * 
 * This React application allows predicting electricity production
 * from photovoltaic solar panels using environmental data
 * and different prediction models.
 * 
 * Main features:
 * - React architecture with React Router for navigation
 * - JWT authentication system
 * - Reactive user interface with Tailwind CSS
 * - Communication with Node.js/Express backend API
 * - Solar production data visualization
 *
 * Developed by: [Your Name]
 * Date: July 2025
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext'; // Global authentication context
import { AppStateProvider } from './context/AppStateContext'; // Global application state context
import { PredictStateProvider } from './context/PredictStateContext'; // Prediction page state context

// Layout and common components for general application structure
import Navbar from './components/Navbar';   // Navigation bar with logo and menu
import Footer from './components/Footer';   // Footer with legal information

// Import of different application pages
// I structured the application into distinct pages for better organization
// and clear separation of responsibilities
import Home from './pages/Home';           // Home page with presentation
import Login from './pages/Login';         // User authentication
import Register from './pages/Register';   // Account creation
import Predict from './pages/Predict';     // Main solar prediction page
// import ResultDetail from './pages/ResultDetail'; // Detailed results display
// import Modules from './pages/Modules';     // Photovoltaic modules management
// import ModuleForm from './pages/ModuleForm'; // Module creation/modification
import About from './pages/About';         // About page with project information
import Info from './pages/Info';           // Additional information
import History from './pages/History';     // Prediction history
import Help from './pages/Help';           // User help page

/**
 * Route protection component
 * 
 * This component is used to protect routes that require authentication.
 * It checks if the user is authenticated via the authentication context.
 * If the user is not authenticated, they are redirected to the login page.
 * If authentication verification is in progress, displays a loading spinner.
 * 
 * @param {Object} children - Child components to display if authentication succeeds
 * @returns {JSX.Element} Child component or a redirect
 */
const ProtectedRoute = ({ children }) => {
  // Get authentication state from context
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  
  // Display spinner during JWT token verification
  if (loading) {
    return <div className="flex justify-center items-center h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600"></div>
    </div>;
  }
  
  // If not authenticated, redirect to login page with current location
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // If authenticated, display protected content
  return children;
};

/**
 * Main application component
 * 
 * This component defines the global structure of the application with:
 * - Authentication system via AuthProvider
 * - Router for navigation between pages
 * - Common layout structure for all pages
 * - Public and protected routes system
 * 
 * I organized the routes to offer an intuitive user experience
 * while protecting features that require authentication.
 */
function App() {
  return (
    // Provides authentication context to the entire application
    <AuthProvider>
      {/* Provides global application state context */}
      <AppStateProvider>
        {/* Provides prediction page state context */}
        <PredictStateProvider>
        {/* Configure router for navigation */}
        <Router>
          {/* Main structure with flex so footer stays at bottom */}
          <div className="flex flex-col min-h-screen bg-gray-50">
            <Navbar />
            <main className="flex-grow pt-16"> {/* Spacing for fixed navbar */}
              <Routes>
              {/* Public routes accessible without authentication */}
              {/* These pages allow all users to discover the application */}
              <Route path="/" element={<Home />} />                 {/* Home page */}
              <Route path="/login" element={<Login />} />           {/* Login */}
              <Route path="/register" element={<Register />} />     {/* Registration */}
              <Route path="/about" element={<About />} />           {/* About */}
              <Route path="/info" element={<Info />} />             {/* Information */}
              <Route path="/help" element={<Help />} />             {/* Help */}
              
              {/* Protected routes requiring authentication */}
              {/* The functional core of the application is protected */}
              
              {/* Main prediction page */}
              <Route path="/predict" element={
                <ProtectedRoute>
                  <Predict />
                </ProtectedRoute>
              } />
              {/* Prediction history management */}
              {/* These routes allow access to past predictions */}
              
              {/* List of all predictions */}
              <Route path="/history" element={
                <ProtectedRoute>
                  <History />
                </ProtectedRoute>
              } />
              
              {/* Specific prediction detail */}
              <Route path="/history/:id" element={
                <ProtectedRoute>
                </ProtectedRoute>
              } />

              
              {/* Unknown routes handling */}
              {/* Redirect to home page if URL doesn't match any route */}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </main>
          <Footer />
        </div>
      </Router>
        </PredictStateProvider>
      </AppStateProvider>
    </AuthProvider>
  );
}

export default App;
